const ROWS = ['qwertyuiop', 'asdfghjkl', 'zxcvbnm'] as const

export interface KeyboardProps {
  guessed: readonly string[]
  /** Letters that turned out to be in the word. */
  correct: readonly string[]
  disabled: boolean
  onGuess: (letter: string) => void
}

/**
 * The letter picker, in QWERTY.
 *
 * Deliberately the real keyboard layout rather than alphabetical order: she
 * will spend years typing, and the muscle memory is worth building while the
 * stakes are a game of Hangman.
 *
 * That fixes the geometry, and it is worth being clear about what it costs.
 * Ten keys cannot be 44px wide on a 360px phone — that is 440px before any
 * gaps — so the width guideline is unreachable for this layout. No shipping
 * phone keyboard manages it either; iOS and Android sit around 32px and make
 * up for it with height and spacing. This does the same:
 *
 *  - height comfortably above the 44px minimum, since vertical slop is what
 *    actually catches a thumb aiming at the wrong row
 *  - keys share the row evenly and take all the width available, so a wider
 *    phone or a tablet gets bigger keys for free
 *  - touch-action manipulation, which removes the 300ms double-tap-zoom delay
 *    that otherwise makes every key feel unresponsive
 */
export function Keyboard({ guessed, correct, disabled, onGuess }: KeyboardProps) {
  return (
    <div
      className="flex w-full max-w-lg flex-col items-center gap-1.5"
      role="group"
      aria-label="Letters"
    >
      {ROWS.map((row) => (
        <div key={row} className="flex w-full justify-center gap-1 sm:gap-1.5">
          {[...row].map((letter) => {
            const used = guessed.includes(letter)
            const hit = correct.includes(letter)

            return (
              <button
                key={letter}
                type="button"
                onClick={() => onGuess(letter)}
                disabled={disabled || used}
                aria-label={letter.toUpperCase()}
                // Colour is backed up by a label and by aria-pressed, so this
                // still works for a player who cannot tell the states apart by
                // hue.
                aria-pressed={used}
                style={{ touchAction: 'manipulation' }}
                className={[
                  'font-display flex flex-1 items-center justify-center uppercase transition-colors',
                  // Taller than the minimum, and as wide as the row allows.
                  'min-h-12 min-w-8 max-w-14 sm:min-h-14',
                  'rounded-(--radius-control) border-2 disabled:cursor-not-allowed',
                  used
                    ? hit
                      ? 'bg-teal-soft text-teal border-teal'
                      : 'bg-surface-sunken text-ink-muted border-border opacity-60'
                    : 'bg-surface-raised border-border hover:border-primary',
                ].join(' ')}
              >
                {letter}
              </button>
            )
          })}
        </div>
      ))}
    </div>
  )
}
